import gluedin from "gluedin-shorts-js";

const userModule = new gluedin.GluedInUserModule();

export interface CoinPackageItem {
  id: string;
  packageId: string;
  packName: string;
  coins: number;
  price: number;
  priceLabel: string;
  status: string;
  googleSkuId: string;
  appleSkuId: string;
}

let coinPackagesCache: CoinPackageItem[] | null = null;
let coinPackagesInflight: Promise<CoinPackageItem[]> | null = null;

const toNumber = (value: unknown): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatPriceLabel = (value: unknown): string => {
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return "$0.00";
    const parsed = Number(trimmed.replace(/,/g, ""));
    if (Number.isFinite(parsed)) {
      return `$${parsed.toFixed(2)}`;
    }
    return trimmed;
  }
  const parsed = Number(value);
  if (Number.isFinite(parsed)) {
    return `$${parsed.toFixed(2)}`;
  }
  return "$0.00";
};

const normalizeCoinPackages = (response: any): CoinPackageItem[] => {
  const rawList =
    response?.data?.result ?? response?.result ?? response?.data?.data ?? [];

  if (!Array.isArray(rawList)) {
    return [];
  }

  return rawList
    .filter(
      (item: any) => String(item?.status || "").toUpperCase() === "ACTIVE",
    )
    .map((item: any, index: number) => {
      const packageId = String(item?.packageId || item?._id || index + 1);
      return {
        id: packageId,
        packageId,
        packName: String(item?.packName || ""),
        coins: toNumber(item?.coin),
        price: toNumber(item?.price),
        priceLabel: formatPriceLabel(item?.price),
        status: String(item?.status || ""),
        googleSkuId: String(item?.googleSkuId || ""),
        appleSkuId: String(item?.appleSkuId || ""),
      };
    })
    .filter((item: CoinPackageItem) => item.coins > 0)
    .sort((a: CoinPackageItem, b: CoinPackageItem) => {
      if (a.coins !== b.coins) return a.coins - b.coins;
      return a.price - b.price;
    });
};

export const getUserCoinPackages = async (
  forceRefresh = false,
): Promise<CoinPackageItem[]> => {
  if (!forceRefresh && coinPackagesCache) {
    return coinPackagesCache;
  }

  if (!forceRefresh && coinPackagesInflight) {
    return coinPackagesInflight;
  }

  coinPackagesInflight = userModule
    .getUserCoinPackageList()
    .then((response: any) => {
      const packages = normalizeCoinPackages(response);
      coinPackagesCache = packages;
      return packages;
    })
    .finally(() => {
      coinPackagesInflight = null;
    });

  return coinPackagesInflight;
};
